import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosPublicInstance from "../../services/axiosPublicInstance";
import axiosInstance from "../../services/axiosInstance";
import * as Endpoints from "../../entities/Endpoints";
import axiosStripePublicInstance from "../../services/axiosStripePublicInstance";

// Async Thunk for Setting Up Stripe for Coach
export const getSetupStripeForCoachThunk = createAsyncThunk(
  `${Endpoints.SET_UP_STRIPE_FOR_COACH}`,
  async ({ coachId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${Endpoints.SET_UP_STRIPE_FOR_COACH}/${coachId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch lead offer details"
      );
    }
  }
);

// Async Thunk for Lead Offer Details
export const getLeadOfferDetails = createAsyncThunk(
  `${Endpoints.GET_LEAD_OFFER_DETAILS_BY_ID}`,
  async ({ leadOfferDetailsId }, { rejectWithValue }) => {
    try {
      const response = await axiosPublicInstance.get(
        `${Endpoints.GET_LEAD_OFFER_DETAILS_BY_ID}/${leadOfferDetailsId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch lead offer details"
      );
    }
  }
);

// Async Thunk for Checkout Session
export const createCheckoutSession = createAsyncThunk(
  `${Endpoints.GET_CLIENT_CHECKOUT_SESSION}`,
  async ({ leadOfferDetailsId }, { rejectWithValue }) => {
    try {
      const response = await axiosStripePublicInstance.post(
        `${Endpoints.GET_CLIENT_CHECKOUT_SESSION}?offerID=${leadOfferDetailsId}`
      );
      if(response.data.type==='failed'){
      return rejectWithValue(
    <div className="flex justify-center items-center h-screen bg-gray-100">
      <div className="bg-white shadow-lg rounded-2xl p-8 text-center max-w-md">
        <h2 className="text-3xl font-bold text-red-600 mb-4">
          Failed ❌
        </h2>
        <p className="text-xl text-gray-700 mb-6">
          {response.data.message}
        </p>
      </div>
    </div>
  );
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create checkout session"
      );
    }
  }
);

// Async Thunk for Adding Customer Payment Method
export const addCustomerPaymentMethod = createAsyncThunk(
  `{${Endpoints.ADD_CUSTOMER_PAYMENT_METHOD_STRIPE_FOR_COACH}}`,
  async ({ sessionId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${Endpoints.ADD_CUSTOMER_PAYMENT_METHOD_STRIPE_FOR_COACH}/${sessionId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add customer payment method"
      );
    }
  }
);

// Async Thunk for Adding Customer Payment Method
export const addCustomerPaymentMethodForClient = createAsyncThunk(
  `{${Endpoints.ADD_CUSTOMER_PAYMENT_METHOD_STRIPE_FOR_COACH}}`,
  async ({ sessionId, leadOfferDetailsId }, { rejectWithValue }) => {
    try {
      const response = await axiosStripePublicInstance.post(
        `${Endpoints.ADD_CUSTOMER_PAYMENT_METHOD_STRIPE_FOR_CLIENT}?checkoutSession_id=${sessionId}&leadofferDetailID=${leadOfferDetailsId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add customer payment method"
      );
    }
  }
);

export const updateClientPaymentMethod = createAsyncThunk(
  "updateClientPaymentMethod",
  async ({ sessionId }, { rejectWithValue }) => {
    try {
      //  Send sessionId in query string
      const response = await axiosStripePublicInstance.post(
        `${Endpoints.UPDATE_CLIENT_PAYMENT_METHOD}?checkoutSessionId=${sessionId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update client payment method"
      );
    }
  }
);


// Async Thunk for Coach Offer Details
export const getCoachOfferDetails = createAsyncThunk(
  `${Endpoints.GET_LEAD_OFFER_COACH}`,
  async ({ leadOfferDetailsId }, { rejectWithValue }) => {
    try {
      const response = await axiosStripePublicInstance.get(
        `${Endpoints.GET_LEAD_OFFER_COACH}?coachleadofferID=${leadOfferDetailsId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch coach offer details"
      );
    }
  }
);

// Async Thunk for Coach Checkout Session
export const createCoachCheckoutSession = createAsyncThunk(
  `${Endpoints.GET_COACH_CHECKOUT_SESSION}`,
  async ({ leadOfferDetailsId }, { rejectWithValue }) => {
    try {
      const response = await axiosStripePublicInstance.get(
        `${Endpoints.GET_COACH_CHECKOUT_SESSION}?coachleadofferID=${leadOfferDetailsId}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create checkout session"
      );
    }
  }
);

// Async Thunk for Adding Customer Payment Method
export const addCoachPaymentMethodForAdmin = createAsyncThunk(
  `{${Endpoints.POST_PAYMENT_METHOD_STRIPE_FOR_LEAD_SUCCESS_COACH}}`,
  async ({ sessionId, coachleadofferID }, { rejectWithValue }) => {
    try {
      const response = await axiosStripePublicInstance.post(
        `${Endpoints.POST_PAYMENT_METHOD_STRIPE_FOR_LEAD_SUCCESS_COACH}?checkoutSession_id=${sessionId}&coachleadofferID=${coachleadofferID}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add customer payment method"
      );
    }
  }
);

const leadOfferSlice = createSlice({
  name: "leadOffer",
  initialState: {
    offerData: null,
    coachOfferData: null,
    customerData: null,
    checkoutSession: null,
    coachCheckoutSession: null,
    stripeSetup: null,
    loading: false,
    errorLeadOfferDetails: false,
    errorCoachOfferDetails: false,
    errorCoachCheckoutSession: false,
    stripeSetupError: null,
    error: null,
    success: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getLeadOfferDetails.pending, (state) => {
        state.loading = true;
        state.errorLeadOfferDetails = null;
        state.success = null;
      })
      .addCase(getLeadOfferDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.offerData = action.payload;
        state.success = "Lead offer details fetched successfully";
        state.errorLeadOfferDetails = null;
      })
      .addCase(getLeadOfferDetails.rejected, (state, action) => {
        state.loading = false;
        state.errorLeadOfferDetails = action.payload;
        state.success = null;
      })

      // Add the stripe setup cases
      .addCase(getSetupStripeForCoachThunk.pending, (state) => {
        state.loading = true;
        state.stripeSetupError = null;
        state.success = null;
      })
      .addCase(getSetupStripeForCoachThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.stripeSetup = action.payload;
        state.success = "Stripe setup completed successfully";
        state.stripeSetupError = null;
      })
      .addCase(getSetupStripeForCoachThunk.rejected, (state, action) => {
        state.loading = false;
        state.stripeSetupError = action.payload;
        state.success = null;
      })

      // Rest of your existing cases...
      .addCase(createCheckoutSession.pending, (state) => {
        state.loading = true;
        state.errorLeadOfferDetails = null;
        state.success = null;
      })
      .addCase(createCheckoutSession.fulfilled, (state, action) => {
        state.loading = false;
        state.checkoutSession = action.payload;
        state.success = "Checkout session created successfully";
        state.error = null;
      })
      .addCase(createCheckoutSession.rejected, (state, action) => {
        state.loading = false;
        state.errorLeadOfferDetails = action.payload;
        state.success = null;
      })
      .addCase(addCustomerPaymentMethod.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(addCustomerPaymentMethod.fulfilled, (state, action) => {
        state.loading = false;
        state.customerData = action.payload;
        state.success = "Customer payment method added successfully";
        state.error = null;
      })
      .addCase(addCustomerPaymentMethod.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = null;
      })
      // Coach Offer Details cases
      .addCase(getCoachOfferDetails.pending, (state) => {
        state.loading = true;
        state.errorCoachOfferDetails = null;
        state.success = null;
      })
      .addCase(getCoachOfferDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.coachOfferData = action.payload;
        state.success = "Coach offer details fetched successfully";
        state.errorCoachOfferDetails = null;
      })
      .addCase(getCoachOfferDetails.rejected, (state, action) => {
        state.loading = false;
        state.errorCoachOfferDetails = action.payload;
        state.success = null;
      })

      // Coach Checkout Session cases
      .addCase(createCoachCheckoutSession.pending, (state) => {
        state.loading = true;
        state.errorCoachCheckoutSession = null;
        state.success = null;
      })
      .addCase(createCoachCheckoutSession.fulfilled, (state, action) => {
        state.loading = false;
        state.coachCheckoutSession = action.payload;
        state.success = "Coach checkout session created successfully";
        state.errorCoachCheckoutSession = null;
      })
      .addCase(createCoachCheckoutSession.rejected, (state, action) => {
        state.loading = false;
        state.errorCoachCheckoutSession = action.payload;
        state.success = null;
      });
  },
});

export default leadOfferSlice.reducer;
